import { type PivotTableViewColumn } from '@/types/schema/views/PivotTableView';
import { type ChartRecord } from '@/types/schema/views/ReportView';

export const CALCULATION_PREFIX = 'calc_';
export const RAW_PREFIX = 'raw_';
export const LIMIT_BEFORE_GROUPING = 10;

// Helper functions for field type checking
export const isCalculationOrRawField = (key: string): boolean =>
  key.startsWith(CALCULATION_PREFIX) || key.startsWith(RAW_PREFIX);

export const isCalculationField = (key: string): boolean => key.startsWith(CALCULATION_PREFIX);

// Helper functions for field operations
export const getCalculationRawFields = (records: ChartRecord[]): string[] => {
  const fields = new Set<string>();
  records.forEach((record) => {
    Object.keys(record).forEach((key) => {
      if (isCalculationOrRawField(key)) {
        fields.add(key);
      }
    });
  });
  return Array.from(fields);
};

export const initializeRecordWithFields = (
  baseRecord: Partial<ChartRecord>,
  fields: string[]
): ChartRecord => {
  const record = { ...baseRecord };
  fields.forEach((field) => {
    record[field] = 0;
  });
  return record as ChartRecord;
};

export const sumNumericFields = (
  target: ChartRecord,
  source: ChartRecord,
  fields: string[]
): ChartRecord => {
  const result = { ...target };
  fields.forEach((field) => {
    if (typeof result[field] === 'number' && typeof source[field] === 'number') {
      result[field] = (result[field] as number) + (source[field] as number);
    }
  });
  return result;
};

export const createBaseColumn = (): Pick<
  PivotTableViewColumn,
  | 'grouping'
  | 'link_type'
  | 'ignore_summary'
  | 'rules'
  | 'conn_separator'
  | 'conn_link'
  | 'width'
  | 'type'
  | 'truncate'
> => ({
  grouping: false,
  link_type: undefined,
  ignore_summary: false,
  rules: [],
  conn_separator: '',
  conn_link: '',
  width: {
    type: 'default',
    units: 'px',
    amount: '50'
  },
  type: 'field',
  truncate: false
});

// Data processing helpers
export const processCalculationValues = (
  record: ChartRecord,
  processor: (value: number, key: string) => number | string
): ChartRecord => {
  if (!record) {
    throw new Error('Record cannot be null or undefined');
  }

  const newRecord = { ...record };

  Object.keys(record).forEach((key) => {
    if (isCalculationField(key) && typeof record[key] === 'number') {
      newRecord[key] = processor(record[key] as number, key);
    }
  });

  return newRecord;
};

export const processCalculationOrRawValues = (
  record: ChartRecord,
  processor: (value: number, key: string) => number | string
): ChartRecord => {
  const newRecord = { ...record };

  Object.keys(record).forEach((key) => {
    if (isCalculationOrRawField(key) && typeof record[key] === 'number') {
      newRecord[key] = processor(record[key] as number, key);
    }
  });

  return newRecord;
};
